import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QueueJobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: any;
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

/**
 * Hook for submitting analysis jobs to background queue
 * Prevents timeouts and handles high load gracefully
 */
export function useAnalysisQueue() {
  const [submitting, setSubmitting] = useState(false);
  const [pendingJobs, setPendingJobs] = useState<string[]>([]);

  const submitJob = useCallback(async (
    imageData: string,
    options: {
      context?: string;
      source?: 'PHONE' | 'DRONE' | 'TEST';
      priority?: number;
      jobId?: string;
    } = {}
  ): Promise<string | null> => {
    setSubmitting(true);
    try {
      // Call edge function with rate limiting and caching
      const { data, error } = await supabase.functions.invoke('submit-analysis-job', {
        body: {
          imageData,
          context: options.context,
          source: options.source,
          priority: options.priority,
          jobId: options.jobId
        }
      });

      if (error) {
        // Handle rate limit errors specifically
        if (error.message?.includes('Rate limit')) {
          toast.error('Queue is busy', {
            description: 'Please wait a moment and try again'
          });
        } else {
          toast.error('Failed to queue analysis');
        }
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit job');
      }

      // Check if result was cached
      if (data.cached) {
        console.log(`✅ Cache hit! Result returned instantly`);
        toast.success('Analysis retrieved from cache', {
          description: `Result from ${data.cacheAge}s ago`
        });
        // For cached results, return a special marker
        return `cached:${Date.now()}`;
      }

      console.log(`✅ Job ${data.jobId} submitted to queue`);
      setPendingJobs(prev => [...prev, data.jobId]);
      
      return data.jobId;
    } catch (error) {
      console.error('Failed to submit job:', error);
      return null;
    } finally {
      setSubmitting(false);
    }
  }, []);

  /**
   * Check status of a queued job
   */
  const checkJobStatus = useCallback(async (jobId: string): Promise<QueueJobStatus | null> => {
    try {
      const { data, error } = await supabase
        .from('analysis_job_queue')
        .select('id, status, result, error_message, created_at, completed_at')
        .eq('id', jobId)
        .single();

      if (error) throw error;

      return data as QueueJobStatus;
    } catch (error) {
      console.error('Failed to check job status:', error);
      return null;
    }
  }, []);

  /**
   * Poll for job completion
   */
  const waitForJob = useCallback(async (
    jobId: string,
    options: { timeout?: number; pollInterval?: number } = {}
  ): Promise<any | null> => {
    const { timeout = 30000, pollInterval = 1000 } = options;
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const status = await checkJobStatus(jobId);
      
      if (!status) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      if (status.status === 'completed') {
        setPendingJobs(prev => prev.filter(id => id !== jobId));
        return status.result;
      }

      if (status.status === 'failed') {
        setPendingJobs(prev => prev.filter(id => id !== jobId));
        throw new Error(status.error_message || 'Job failed');
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Job timeout - still processing');
  }, [checkJobStatus]);

  /**
   * Trigger background queue processing (best effort)
   */
  const triggerQueueProcessing = useCallback(async () => {
    try {
      // Fire and forget - don't wait for response
      supabase.functions.invoke('process-analysis-queue', {
        body: { batchSize: 10 }
      }).catch(err => console.warn('Queue processing trigger failed:', err));
    } catch (error) {
      console.warn('Failed to trigger queue processing:', error);
    }
  }, []);

  /**
   * Get pending jobs for current user
   */
  const getPendingJobs = useCallback(async (): Promise<QueueJobStatus[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('analysis_job_queue')
        .select('id, status, result, error_message, created_at, completed_at')
        .eq('user_id', user.id)
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data as QueueJobStatus[]) || [];
    } catch (error) {
      console.error('Failed to get pending jobs:', error);
      return [];
    }
  }, []);

  return {
    submitJob,
    checkJobStatus,
    waitForJob,
    getPendingJobs,
    submitting,
    pendingJobsCount: pendingJobs.length,
    triggerQueueProcessing
  };
}
